// ============ NotificationSection.jsx ============
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

import { shakeAnimation } from '../../../../utils/animation';
import { markAllAsRead, sendTestNotificationToDEV } from '../../../../services/notificationAPIs';
import { connectWebSocket } from '../../../../api';
import { motion } from 'framer-motion';
import { getUnreadNotificationCount, hasUnreadNotifications } from '../../../../utils/notifications';

import MainCard from '../../../../components/cards/MainCard';
import Transitions from '../../../../components/extended/Transitions';
import NotificationList from './NotificationList';
import { getMyNotifications } from '../../../../services/notificationAPIs';

import { IconBell, IconBellRinging } from '@tabler/icons-react';
import notificationSound from '../../../../assets/sounds/notification_sound.wav';

const status = [
  { value: 'all', label: 'All Notification' },
  { value: 'new', label: 'New' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' }
];

export default function NotificationSection({ allow }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [AXONotifications, setAXONotifications] = useState([]);
  const [myNotifications, setMyNotifications] = useState([]);
  const [shouldShake, setShouldShake] = useState(false);
  const prevNotificationCount = useRef(0);

  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  // const handleViewAll = () => {
  //   setOpen(false);
  //   navigate('/notifications');
  // };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleChange = (event) => {
    event?.target.value && setValue(event?.target.value);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchNotifications = async (triggerSoundOnNew = false) => {
      try {
        const myData = await getMyNotifications();
        if (!isMounted) return;

        setMyNotifications(myData);

        const myAxoData = (myData || []).filter(
          (notification) => notification?.channel === 'AXO'
        );

        const unreadExists = hasUnreadNotifications(myAxoData);
        setAXONotifications(myAxoData);
        setShouldShake(unreadExists);

        // Play sound and trigger shake if a brand new unread notification arrived
        if (
          triggerSoundOnNew &&
          myAxoData.length > prevNotificationCount.current &&
          unreadExists
        ) {
          try {
            const audio = new Audio(notificationSound);
            audio.play().catch((e) => console.log('Notification audio autoplay prevented:', e));
          } catch (_) {}
        }

        prevNotificationCount.current = myAxoData.length;
      } catch (error) {
        console.error('Failed to fetch notifications', error);
        if (isMounted) setShouldShake(false);
      }
    };

    // 1. Initial fetch on mount
    fetchNotifications(false);

    // 2. Real-time WebSocket connection & topic subscription
    const unsubscribe = connectWebSocket((topic, payload) => {
      if (topic === 'notifications') {
        const currentEmpId = localStorage.getItem('employeeId');

        // Verify if notification targets current user or whole role/system
        let isRelevant = true;
        if (currentEmpId && payload) {
          if (Array.isArray(payload)) {
            isRelevant = payload.some(
              (item) => !item.employeeId || String(item.employeeId) === String(currentEmpId)
            );
          } else if (payload.employeeId) {
            isRelevant = String(payload.employeeId) === String(currentEmpId);
          }
        }

        if (isRelevant) {
          console.log('⚡ Real-time notification received via WebSocket!');
          fetchNotifications(true);
        }
      }
    });

    // 3. Fallback background sync every 30 seconds
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications(false);
      }
    }, 30000);

    // 4. Tab visibility sync: immediately refresh when user focuses or returns to tab
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      isMounted = false;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, []);


  const triggerTestNotification = () => {
    const mockData = {
      id: Date.now(),
      message: '🔔 Test Notification: Sound System Active!',
      channel: 'AXO',
      timestamp: new Date().toISOString(),
      userNotifications: [{ read: false }]
    };
    setAXONotifications((prev) => [mockData, ...prev]);
    setShouldShake(true);
    const audio = new Audio(notificationSound);
    audio.play().catch(e => console.error("Sound play failed:", e));
  };


  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setShouldShake(false);
      setNotifications([]);
      setAXONotifications([]);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleSendTestDEV = async () => {
    try {
      await sendTestNotificationToDEV();
    } catch (error) {
      console.error('Failed to send test notification:', error);
    }
  };

  const isToday = (dateStr) => {
    const today = new Date();
    const date = new Date(dateStr);
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const filterNotifications = (AXONotifications) => {
    if (value === 'all') return AXONotifications;
    if (value === 'new') return AXONotifications.filter((n) => isToday(n.timestamp));
    if (value === 'unread') return AXONotifications.filter((n) => !n.read);
    if (value === 'read') return AXONotifications.filter((n) => n.read);
    return AXONotifications;
  };

  const unreadCount = getUnreadNotificationCount(AXONotifications);


  return (
    <>
      <Box sx={{ ml: 2 }}>
        {allow && (
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 600,
                fontSize: '0.65rem',
                minWidth: '18px',
                height: '18px',
                padding: '0 4px',
                animation: shouldShake ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.9
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1
                  }
                }
              }
            }}
          >

            <Avatar
              variant="rounded"
              sx={{
                ...theme.typography.commonAvatar,
                ...theme.typography.mediumAvatar,
                transition: 'all .3s ease',
                bgcolor: open
                  ? theme.palette.secondary.dark
                  : alpha(theme.palette.secondary.light, 0.9),
                color: open ? theme.palette.secondary.light : theme.palette.secondary.dark,
                border: `2px solid ${alpha(theme.palette.secondary.main, open ? 0.4 : 0.1)}`,
                boxShadow: open
                  ? `0 0 20px ${alpha(theme.palette.secondary.main, 0.3)}`
                  : 'none',
                '&:hover': {
                  bgcolor: theme.palette.secondary.dark,
                  color: theme.palette.secondary.light,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`
                },
                position: 'relative'
              }}
              ref={anchorRef}
              aria-controls={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              color="inherit"
            >
              <motion.div
                variants={shakeAnimation}
                animate={shouldShake ? 'animate' : 'stop'}
              >
                {shouldShake ? (
                  <IconBellRinging stroke={1.5} size="20px" />
                ) : (
                  <IconBell stroke={1.5} size="20px" />
                )}
              </motion.div>
            </Avatar>
          </Badge>
        )}

        {!allow && (
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .3s ease',
              bgcolor: alpha(theme.palette.error.light, 0.2),
              color: theme.palette.error.main,
              border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                bgcolor: alpha(theme.palette.error.light, 0.3),
                transform: 'translateY(-2px)'
              },
              position: 'relative'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
          >
            <IconBell stroke={1.5} size="20px" />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-45deg)',
                fontSize: 28,
                fontWeight: 'bold',
                color: theme.palette.error.main,
                pointerEvents: 'none',
                zIndex: 10,
                textShadow: `0 0 4px ${theme.palette.background.paper}`
              }}
            >
              /
            </Box>
          </Avatar>
        )}
      </Box>

      {allow && (
        <Popper
          placement={downMD ? 'bottom' : 'bottom-end'}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [downMD ? 5 : 0, 20]
              }
            }
          ]}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Transitions
                position={downMD ? 'top' : 'top-right'}
                in={open}
                {...TransitionProps}
              >
                <Paper
                  elevation={12}
                  sx={{
                    background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${alpha(theme.palette.secondary.light, 0.02)})`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    minWidth: 340
                  }}
                >
                  {open && (
                    <MainCard
                      border={false}
                      elevation={0}
                      content={false}
                      boxShadow={false}
                      sx={{ background: 'var(--card-bg, white)' }}
                    >
                      <Grid container direction="column" spacing={0}>
                        {/* Header */}
                        <Grid size={12}>
                          <Box
                            sx={{
                              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)}, ${alpha(theme.palette.secondary.light, 0.05)})`,
                              p: 2.5,
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              position: 'relative',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`
                              }
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <IconBellRinging
                                  size={22}
                                  color={theme.palette.secondary.main}
                                />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                  Notifications
                                </Typography>
                                <Chip
                                  size="small"
                                  label={AXONotifications.length}
                                  sx={{
                                    height: 24,
                                    fontWeight: 600,
                                    background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                    color: 'white',
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.3)}`
                                  }}
                                />
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                {localStorage.getItem('userRole') === 'DEV' && (
                                  <>
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={() => {
                                        setOpen(false);
                                        navigate('/dev/notifications');
                                      }}
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        color: theme.palette.primary.main,
                                        px: 1.5,
                                        borderRadius: 2,
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.primary.main, 0.1)
                                        }
                                      }}
                                    >
                                      Dispatch
                                    </Button>
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={handleSendTestDEV}
                                      sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: theme.palette.warning.main,
                                        px: 1.5,
                                        borderRadius: 2,
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.warning.main, 0.1)
                                        }
                                      }}
                                    >
                                      Test
                                    </Button>
                                  </>
                                )}
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={handleMarkAllAsRead}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: theme.palette.secondary.main,
                                    px: 1.5,
                                    borderRadius: 2,
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1)
                                    }
                                  }}
                                >
                                  Mark all read
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>
                        </Grid>

                        {/* Filter */}
                        <Grid size={12}>
                          <Box sx={{ px: 2, pt: 2 }}>
                            <TextField
                              id="notification-filter"
                              select
                              fullWidth
                              size="small"
                              value={value}
                              onChange={handleChange}
                              slotProps={{ select: { native: true } }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  bgcolor: alpha(theme.palette.secondary.light, 0.05),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.secondary.light, 0.08)
                                  },
                                  '&.Mui-focused': {
                                    bgcolor: alpha(theme.palette.secondary.light, 0.1)
                                  }
                                }
                              }}
                            >
                              {status.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </TextField>
                          </Box>
                        </Grid>

                        <Grid size={12}>
                          <Divider sx={{ my: 1.5, opacity: 0.6 }} />
                        </Grid>

                        {/* Notification List */}
                        <Grid size={12}>
                          <Box
                            sx={{
                              height: '100%',
                              maxHeight: 'calc(100vh - 280px)',
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              '&::-webkit-scrollbar': {
                                width: 6
                              },
                              '&::-webkit-scrollbar-track': {
                                bgcolor: alpha(theme.palette.grey[300], 0.2),
                                borderRadius: 10
                              },
                              '&::-webkit-scrollbar-thumb': {
                                bgcolor: alpha(theme.palette.secondary.main, 0.3),
                                borderRadius: 10,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.secondary.main, 0.5)
                                }
                              }
                            }}
                          >
                            {AXONotifications.length > 0 ? (
                              <NotificationList
                                notifications={notifications}
                                AXONotifications={filterNotifications(AXONotifications)}
                                setNotifications={setNotifications}
                              />
                            ) : (
                              <Box
                                sx={{
                                  py: 6,
                                  px: 3,
                                  textAlign: 'center',
                                  color: theme.palette.text.secondary
                                }}
                              >
                                <IconBell size={48} stroke={1} opacity={0.3} />
                                <Typography variant="body2" sx={{ mt: 2 }}>
                                  No notifications yet
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Footer */}
                      <CardActions
                        sx={{
                          p: 2,
                          justifyContent: 'center',
                          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          bgcolor: alpha(theme.palette.secondary.light, 0.03)
                        }}
                      >
                        {/* <Button
                          size="medium"
                          disableElevation
                          onClick={handleViewAll}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            color: theme.palette.secondary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.secondary.main, 0.1)
                            }
                          }}
                        >
                          View All Notifications
                        </Button> */}
                      </CardActions>
                    </MainCard>
                  )}
                </Paper>
              </Transitions>
            </ClickAwayListener>
          )}
        </Popper>
      )}
    </>
  );
}